import React, { useEffect, useState } from 'react';
import { X, MapPin, Phone, Clock, User, Building2, Truck, ChevronDown, ChevronUp } from 'lucide-react';

const AmbulanceBookingConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  patientName, 
  hospitalName, 
  ambulanceType, 
  estimatedFare,
  patientAge,
  phoneNumber,
  hmisId,
  pickupAddress,
  destinationAddress,
  currentCondition,
  emergencyLevel,
  scheduledDateTime,
  bookingType,
  isLoading = false
}) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Use requestAnimationFrame to prevent ResizeObserver issues
      requestAnimationFrame(() => {
        document.body.style.overflow = 'hidden';
      });
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      // Use requestAnimationFrame to prevent ResizeObserver issues
      requestAnimationFrame(() => {
        document.body.style.overflow = 'unset';
      });
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg max-w-md w-full mx-auto shadow-xl overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="bg-green-50 p-4 rounded-t-lg border-b border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-green-100 p-1.5 rounded-full">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-2">
                <h3 className="text-base font-semibold text-green-900">
                  Are you sure you want to book this ambulance?
                </h3>
                <p className="text-xs text-green-700">
                  Please review your booking details before confirming
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-green-400 hover:text-green-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Patient Details Accordion */}
          <div className="border border-gray-200 rounded-lg">
            <button
              onClick={() => setIsAccordionOpen(!isAccordionOpen)}
              className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                  <User className="w-3 h-3 text-gray-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Patient Details</p>
                  <p className="text-sm font-medium text-gray-900">{patientName || 'N/A'}</p>
                </div>
              </div>
              {isAccordionOpen ? (
                <ChevronUp className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              )}
            </button>
            
            {isAccordionOpen && (
              <div className="px-3 pb-3 space-y-2 border-t border-gray-100">
                {/* Patient Name */}
                <div className="flex items-center">
                  <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                    <User className="w-2.5 h-2.5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Patient Name</p>
                    <p className="text-xs font-medium text-gray-900">{patientName || 'N/A'}</p>
                  </div>
                </div>

                {/* Patient Age */}
                {patientAge && (
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                      <Clock className="w-2.5 h-2.5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Patient Age</p>
                      <p className="text-xs font-medium text-gray-900">{patientAge} years</p>
                    </div>
                  </div>
                )}

                {/* Phone Number */}
                {phoneNumber && (
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                      <Phone className="w-2.5 h-2.5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Phone Number</p>
                      <p className="text-xs font-medium text-gray-900">{phoneNumber}</p>
                    </div>
                  </div>
                )}

                {/* HMIS ID */}
                {hmisId && (
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                      <svg className="w-2.5 h-2.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Patient HMIS ID</p>
                      <p className="text-xs font-medium text-gray-900">{hmisId}</p>
                    </div>
                  </div>
                )}

                {/* Pickup Address */}
                {pickupAddress && (
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                      <MapPin className="w-2.5 h-2.5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Pickup Address</p>
                      <p className="text-xs font-medium text-gray-900">{pickupAddress}</p>
                    </div>
                  </div>
                )}

                {/* Destination Address */}
                {destinationAddress && (
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                      <MapPin className="w-2.5 h-2.5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Destination Address</p>
                      <p className="text-xs font-medium text-gray-900">{destinationAddress}</p>
                    </div>
                  </div>
                )}

                {/* Current Condition */}
                {currentCondition && (
                  <div className="flex items-start">
                    <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center mr-2 mt-0.5">
                      <svg className="w-2.5 h-2.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">Current Condition</p>
                      <p className="text-xs font-medium text-gray-900">{currentCondition}</p>
                    </div>
                  </div>
                )}

                {/* Emergency Level */}
                {emergencyLevel && (
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                      <svg className="w-2.5 h-2.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Emergency Level</p>
                      <p className="text-xs font-medium text-gray-900">{emergencyLevel}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Hospital and Ambulance Type */}
          <div className="space-y-2">
            {hospitalName && (
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                  <Building2 className="w-3 h-3 text-gray-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Hospital</p>
                  <p className="text-sm font-medium text-gray-900">{hospitalName}</p>
                </div>
              </div>
            )}

            {ambulanceType && (
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                  <Truck className="w-3 h-3 text-gray-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Ambulance Type</p>
                  <p className="text-sm font-medium text-gray-900">{ambulanceType}</p>
                </div>
              </div>
            )}

            {/* Scheduled Date/Time for Later Bookings */}
            {bookingType === "SCHEDULED" && scheduledDateTime && (
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                  <Clock className="w-3 h-3 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Scheduled Date & Time</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(scheduledDateTime).toLocaleString('en-IN', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center">
              {/* Green circle removed for consistent UI */}
              <div>
                <p className="text-xs text-gray-500">Estimated Fare</p>
                {estimatedFare ? (
                  <p className="text-sm font-bold text-green-900">₹{Math.round(estimatedFare)}</p>
                ) : (
                  <p className="text-sm font-bold text-gray-400">₹--</p>
                )}
                <p className="text-xs text-gray-500">Final amount may vary</p>
              </div>
            </div>
          </div>

          {/* Estimated Fare Highlight */}
          {estimatedFare && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-green-800">Estimated Total Cost</p>
                  <p className="text-xs text-green-600">
                    This is an approximate cost based on distance and ambulance type
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-green-900">₹{Math.round(estimatedFare)}</p>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 rounded-b-lg">
          <div className="flex justify-between items-center">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className={`px-3 py-1.5 text-xs font-medium text-white border border-transparent rounded-md transition-colors flex items-center justify-center gap-2 ${
                isLoading 
                  ? 'bg-purple-400 cursor-not-allowed' 
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating Booking...
                </>
              ) : (
                'Confirm Booking'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmbulanceBookingConfirmationModal;
